import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, PlayCircle, Star, Briefcase, Users, BarChart, FileText, Award, Layers, Clock, Video, BookOpen, Target, Shield, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReadMore from '../components/ReadMore';

const Section = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border-b border-gray-200 dark:border-gray-700 py-6">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center text-left text-xl font-semibold text-gray-800 dark:text-gray-200"
            >
                <span>{title}</span>
                {isOpen ? <ChevronUp className="w-5 h-5 text-primary-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="mt-4 text-gray-700 dark:text-gray-300 space-y-4">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const syllabus = [
    {
        week: 1,
        title: 'Getting Started with Java',
        sessions: ['Introduction to Java', 'Data Types & Variables', 'Operators & Expressions'],
    },
    {
        week: 2,
        title: 'Control Flow',
        sessions: ['Conditional Statements', 'Loops (For, While, Do-While)', 'Break & Continue'],
    },
    {
        week: 3,
        title: 'Object-Oriented Programming (OOP) in Java - Part 1',
        sessions: ['Classes & Objects', 'Constructors', '`this` keyword', 'Inheritance'],
    },
    {
        week: 4,
        title: 'Object-Oriented Programming (OOP) in Java - Part 2',
        sessions: ['Polymorphism', 'Method Overloading & Overriding', 'Abstraction', 'Interfaces & Abstract Classes'],
    },
    {
        week: 5,
        title: 'Core Java Concepts',
        sessions: ['Exception Handling', 'Collections Framework (Lists, Sets, Maps)', 'Generics'],
    },
    {
        week: 6,
        title: 'Data Structures & Algorithms - Part 1',
        sessions: ['Arrays & Strings', 'Linked Lists', 'Stacks & Queues'],
    },
    {
        week: 7,
        title: 'Data Structures & Algorithms - Part 2',
        sessions: ['Trees & Heaps', 'Hashing', 'Introduction to Algorithms'],
    },
    {
        week: 8,
        title: 'Introduction to Backend Development',
        sessions: ['Introduction to Spring & Spring Boot', 'Setting up a Spring Boot Project', 'Spring Boot Annotations'],
    },
    {
        week: 9,
        title: 'Building APIs with Spring Boot',
        sessions: ['RESTful APIs Concepts', 'Creating Controllers & Endpoints', 'Request & Response Handling'],
    },
    {
        week: 10,
        title: 'Databases & Spring Data JPA',
        sessions: ['Introduction to JPA & Hibernate', 'Configuring a Database', 'Repositories & CRUD Operations'],
    },
    {
        week: 11,
        title: 'Advanced Spring Concepts',
        sessions: ['Spring Security Fundamentals', 'Implementing Basic Authentication', 'Dependency Injection & IOC'],
    },
    {
        week: 12,
        title: 'Project & Deployment',
        sessions: ['Building a Capstone Project', 'Testing Your Application', 'Introduction to Deployment (Docker & Cloud)'],
    }
];

const whatSetsUsApart = [
    { icon: <BookOpen className="w-8 h-8 text-primary-500" />, title: 'Complimentary Course Access', description: 'Get access to our premium DSA course for free.' },
    { icon: <Layers className="w-8 h-8 text-primary-500" />, title: 'Major Projects', description: 'Build 2+ major projects to showcase your skills.' },
    { icon: <Clock className="w-8 h-8 text-primary-500" />, title: 'Flexible Learning', description: 'Learn at your own pace with lifetime access to content.' },
    { icon: <Target className="w-8 h-8 text-primary-500" />, title: 'Career Guidance', description: 'Personalized guidance and resume-building sessions.' },
    { icon: <Shield className="w-8 h-8 text-primary-500" />, title: 'Internship Certificate', description: 'Guaranteed internship certificate upon completion.' },
    { icon: <Zap className="w-8 h-8 text-primary-500" />, title: 'Doubt Assistance', description: '24/7 doubt support through our dedicated portal.' },
];

const keyHighlights = [
    '12 weeks of intensive, live online training',
    'Comprehensive curriculum covering Java, DSA, and Spring Boot',
    'Hands-on experience with 2+ real-world projects',
    'Live doubt-clearing sessions with industry experts',
    'A guaranteed Internship Certificate from OSOPCoding',
    'Personalized career guidance and interview preparation',
    'Lifetime access to course materials and recordings',
];

const JobOriented = () => {
    const [isSticky, setIsSticky] = useState(false);
    const heroRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsSticky(!entry.isIntersecting);
            },
            { rootMargin: '-80px 0px 0px 0px' } 
        );

        if (heroRef.current) {
            observer.observe(heroRef.current);
        }

        return () => {
            if (heroRef.current) {
                observer.unobserve(heroRef.current);
            }
        };
    }, []);

    return (
        <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 min-h-screen">
            
            {/* Sticky Header */}
            <AnimatePresence>
                {isSticky && (
                    <motion.div
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -100, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-md h-20 z-40 flex items-center px-6 lg:px-12"
                    >
                        <div className="w-full flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white">JAVA Backend Development - Live</h2>
                            <button className="bg-primary-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-600 transition-colors">
                                Download Brochure
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <main className="max-w-screen-xl mx-auto p-4 md:p-6 lg:p-8 pt-10">
                {/* Breadcrumbs */}
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    <span>All Courses</span> &gt; <span>Live</span> &gt; <span className="font-semibold text-gray-700 dark:text-gray-300">JAVA Backend Development - Live</span>
                </div>

                <div className="lg:grid lg:grid-cols-3 lg:gap-12">
                    
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div ref={heroRef}>
                            <div className="inline-block bg-primary-100 text-primary-800 text-sm font-semibold px-3 py-1 rounded-full mb-3">
                                Live Course
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight">
                                JAVA Backend Development - Live
                            </h1>
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full">
                                    <Star className="w-4 h-4 mr-1 text-green-600" />
                                    <span className="font-bold">4.7/5</span>
                                    <span className="text-sm ml-1">(1.2k+ ratings)</span>
                                </div>
                                <div className="flex items-center text-gray-600 dark:text-gray-300">
                                    <Users className="w-5 h-5 mr-2" />
                                    <span className="font-semibold">328k+</span>
                                    <span className="ml-1">interested Geeks</span>
                                </div>
                            </div>
                            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                                Master backend development with our comprehensive Java and Spring Boot course. Build robust, scalable applications and get job-ready with hands-on projects and expert guidance.
                            </p>
                        </div>

                        <div className="mt-12">
                            <Section title="Course Overview" defaultOpen={true}>
                                <ReadMore>
                                    This job-oriented course is meticulously designed to transform you into a proficient Java Backend Developer. Over 12 weeks, you'll embark on a journey from Java fundamentals to advanced Spring Boot concepts. We focus on a practical, hands-on approach, ensuring you not only understand the theory but can also apply it to build real-world applications. The curriculum covers Data Structures & Algorithms to build a strong logical foundation, followed by in-depth training on Spring, Spring Boot, REST APIs, and database integration with JPA/Hibernate. You will work on multiple projects, including a major capstone project that simulates a real-world industry scenario. Our goal is to make you a confident developer, ready to excel in technical interviews and contribute effectively from day one in a professional environment. Upon successful completion, you will receive a guaranteed internship certificate, adding significant value to your resume. Note: This is a limited-time offer, and the price is non-refundable.
                                </ReadMore>
                            </Section>

                            <Section title="Key Highlights" defaultOpen={true}>
                                <ul className="list-disc list-inside space-y-2 pl-2">
                                    {keyHighlights.map((highlight, index) => (
                                        <li key={index}>{highlight}</li>
                                    ))}
                                </ul>
                            </Section>

                            <Section title="What Sets This Course Apart?" defaultOpen={true}>
                                <div className="grid md:grid-cols-2 gap-6 mt-4">
                                    {whatSetsUsApart.map((item, index) => (
                                        <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                            <div className="flex-shrink-0">{item.icon}</div>
                                            <div>
                                                <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{item.title}</h4>
                                                <p className="text-gray-600 dark:text-gray-400">{item.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                            
                            <Section title="Course Content & Syllabus" defaultOpen={true}>
                                <div className="space-y-4">
                                    {syllabus.map((week) => (
                                        <div key={week.week} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                                            <h4 className="font-bold text-lg text-primary-600 dark:text-primary-400 mb-2">Week {week.week}: {week.title}</h4>
                                            <ul className="list-disc list-inside space-y-1 pl-4 text-gray-600 dark:text-gray-300">
                                                {week.sessions.map((session, index) => (
                                                    <li key={index}>{session}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        </div>
                    </div>

                    {/* Sticky Sidebar */}
                    <div className="lg:col-span-1 mt-10 lg:mt-0">
                        <div className="sticky top-24 space-y-6">
                            <div className="bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
                                <PlayCircle className="w-16 h-16 text-white opacity-70" />
                            </div>

                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                                <div className="grid grid-cols-3 gap-4 text-center mb-6">
                                    <div>
                                        <BarChart className="w-8 h-8 mx-auto text-primary-500 mb-1" />
                                        <p className="font-semibold">Intermediate & Advance</p>
                                        <p className="text-sm text-gray-500">Level</p>
                                    </div>
                                    <div>
                                        <Clock className="w-8 h-8 mx-auto text-primary-500 mb-1" />
                                        <p className="font-semibold">12 Weeks</p>
                                        <p className="text-sm text-gray-500">Duration</p>
                                    </div>
                                    <div>
                                        <Users className="w-8 h-8 mx-auto text-primary-500 mb-1" />
                                        <p className="font-semibold">8 Seats Left!</p>
                                        <p className="text-sm text-gray-500">Limited Batch</p>
                                    </div>
                                </div>
                                <button className="w-full bg-primary-500 text-white font-bold py-3 rounded-lg hover:bg-primary-600 transition-all text-lg">
                                    Login to Register
                                </button>
                                <button className="w-full mt-3 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 font-bold py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all">
                                    Download Brochure
                                </button>
                            </div>

                            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-r-lg" role="alert">
                                <p className="font-bold">Ace interviews with 35% OFF!</p>
                                <p className="text-sm">This is a limited-time offer for the upcoming batch. Call us at <a href="tel:+911234567890" className="font-semibold underline">+91-1234567890</a> for details.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

        </div>
    );
};

export default JobOriented;